import { JWT_KEY, API_BASE_URL } from '@/config/constant';
import { isCoreApi, MAINTENANCE_CONFIG } from '@/config/maintenance';
import { useMaintenanceStore } from '@/store/maintenance';
import { ApiError, BaseResponse, ResponseType, TokenData } from './types';

// API 상수들
const API_HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
} as const;

const API_CONTENT_TYPES = {
  JSON: 'application/json',
} as const;

const ENABLE_API_DEBUG_FLAG = import.meta.env.VITE_ENABLE_API_DEBUG;
const IS_DEBUG_ENABLED =
  typeof ENABLE_API_DEBUG_FLAG !== 'undefined' ? ENABLE_API_DEBUG_FLAG === 'true' : import.meta.env.DEV;
const IS_DEV_ENV = import.meta.env.VITE_APP_ENV === 'development';

const debugLog = (...args: unknown[]) => {
  if (IS_DEBUG_ENABLED) {
    // console.log('[API DEBUG]', ...args);
  }
};

// 토큰 관리 함수들
const TOKEN_KEY = 'acepharm_auth_tokens';

const loadTokens = (): TokenData | null => {
  try {
    const tokens = localStorage.getItem(TOKEN_KEY);
    return tokens ? JSON.parse(tokens) : null;
  } catch {
    return null;
  }
};

const saveTokens = (tokens: TokenData): void => {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
};

const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(JWT_KEY);
};

const getAuthToken = () => loadTokens()?.accessToken;
const getRefreshToken = () => loadTokens()?.refreshToken;

function redirectToLoginIfAllowed() {
  if (window.location.pathname === '/login') {
    return;
  }
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?redirect=${returnUrl}`;
}

// 토큰 갱신 상태 관리
let isRefreshing = false;
let failedRequestsQueue: {
  resolve: (value: ResponseType | PromiseLike<ResponseType>) => void;
  reject: (reason?: any) => void;
}[] = [];

// 토큰 갱신 후 콜백 (store 동기화용)
let onTokenRefreshCallback: ((tokens: TokenData) => void) | null = null;

export const setTokenRefreshCallback = (callback: (tokens: TokenData) => void) => {
  onTokenRefreshCallback = callback;
};

const processQueue = (error: any | null = null) => {
  failedRequestsQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(true as unknown as ResponseType);
    }
  });
  failedRequestsQueue = [];
};

// 응답 데이터 처리 공통 함수
const handleResponse = (responseData: any) => {
  const { isSuccess, code, message, result } = responseData;
  return { isSuccess, code, message, result };
};

const request = async <T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endPoint: string,
  useToken: boolean,
  data?: any,
  isMultipart?: boolean,
  responseType?: 'json' | 'blob',
  retry: number = 0,
): Promise<ResponseType<T>> => {
  // 이미 점검 중 상태인 경우 추가 API 호출 차단 (클라이언트 DoS 및 불필요한 요청 방지)
  if (useMaintenanceStore.getState().isMaintenance && !endPoint.includes('/health/check')) {
    throw new Error('현재 서비스 점검 중입니다.');
  }

  let url = `${API_BASE_URL}${endPoint}`;
  const headers = new Headers({});

  debugLog('REQUEST_START', {
    method,
    endPoint,
    useToken,
    hasPayload: Boolean(data),
    isMultipart: Boolean(isMultipart),
  });

  if (method === 'GET' && data) {
    const queryStringData: Record<string, string> = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = (data as any)[key];
        if (value !== undefined && value !== null) {
          queryStringData[key] = String(value);
        }
      }
    }
    const queryParams = new URLSearchParams(queryStringData).toString();
    url = `${url}?${queryParams}`;
    debugLog('REQUEST_QUERY', { endPoint, queryParams });
  } else if (!isMultipart && responseType !== 'blob') {
    headers.append(API_HEADERS.CONTENT_TYPE, API_CONTENT_TYPES.JSON);
  }

  if (responseType === 'blob') {
    headers.append('Accept', '*/*');
  }

  if (useToken) {
    const accessToken = getAuthToken();
    if (accessToken) {
      headers.append(API_HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
    }
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    cache: 'no-store',
  };

  if (method !== 'GET' && data) {
    fetchOptions.body = isMultipart ? (data as unknown as FormData) : JSON.stringify(data);
    if (isMultipart) {
      headers.delete(API_HEADERS.CONTENT_TYPE);
    }
  }

  // 요청 타임아웃 컨트롤러 (10초)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, MAINTENANCE_CONFIG.API_TIMEOUT_MS);

  fetchOptions.signal = controller.signal;

  try {
    let response: Response;
    try {
      response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      debugLog('FETCH_COMPLETED', { endPoint, status: response.status });
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      // CORS 에러나 네트워크 에러는 fetch 자체가 실패함
      console.error('[API Request] Fetch 실패:', fetchError);
      debugLog('FETCH_ERROR', { endPoint, error: fetchError });

      const isTimeout = fetchError.name === 'AbortError';
      const isNetworkError =
        fetchError.message?.includes('Failed to fetch') ||
        fetchError.message?.includes('NetworkError') ||
        fetchError.message?.includes('네트워크');

      // 핵심 API에서 타임아웃 또는 네트워크 연결 실패 시 헬스체크 트리거
      if (isCoreApi(endPoint) && (isTimeout || isNetworkError)) {
        void useMaintenanceStore.getState().triggerHealthCheck(endPoint);
      }

      if (isTimeout) {
        throw new Error('요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
      }

      if (fetchError.message?.includes('CORS') || fetchError.message?.includes('Failed to fetch')) {
        throw new Error('CORS 에러 또는 네트워크 연결 실패. 서버 설정을 확인해주세요.');
      }
      throw fetchError;
    }

    // 서버 장애성 오류 (502 Gateway, 503 Service Unavailable, 504 Gateway Timeout) 가로채기
    if (response.status >= 502 && response.status <= 504) {
      if (isCoreApi(endPoint)) {
        void useMaintenanceStore.getState().triggerHealthCheck(endPoint);
      }
      throw new Error(`서버 점검 또는 게이트웨이 오류가 발생했습니다. (HTTP ${response.status})`);
    }

    // 응답 처리 (JSON 또는 Blob)
    let responseData: any;
    const contentType = response.headers.get('content-type');

    if (responseType === 'blob') {
      // Blob 응답 처리
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      debugLog('RESPONSE_BLOB', { endPoint, status: response.status, blobSize: blob.size });
      return handleResponse({
        isSuccess: true,
        code: response.status,
        message: 'Success',
        result: blob as unknown as T,
      });
    }

    // JSON 응답 처리
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
        debugLog('RESPONSE_JSON', { endPoint, status: response.status, body: responseData });
      } catch (jsonError) {
        const text = await response.text();
        debugLog('RESPONSE_JSON_PARSE_ERROR', { endPoint, preview: text.substring(0, 100) });
        throw new Error(`JSON 파싱 실패: ${text.substring(0, 100)}`);
      }
    } else {
      const text = await response.text();
      debugLog('RESPONSE_UNEXPECTED_CONTENT', { endPoint, preview: text.substring(0, 100) });
      throw new Error(`예상치 못한 응답 형식: ${text.substring(0, 100)}`);
    }

    if (responseData.code === 401 && useToken) {
      debugLog('TOKEN_EXPIRED_DETECTED', { endPoint, isRefreshing, retryCount: retry });

      // 재시도 횟수 제한 (최대 1회)
      if (retry >= 1) {
        console.error('[API Request] 최대 재시도 횟수 초과 (401)');
        debugLog('TOKEN_EXPIRED_MAX_RETRY_REACHED', { endPoint });

        // 토큰 삭제 및 상단으로 알림 (루프 중단)
        clearTokens();
        redirectToLoginIfAllowed();

        return handleResponse({
          isSuccess: false,
          code: 401,
          message: '인증이 만료되었습니다. 다시 로그인해주세요.',
          result: null,
        });
      }

      if (isRefreshing) {
        debugLog('TOKEN_EXPIRED_QUEUE_WAIT', { endPoint });
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve: value => {
              // 재시도 시 원래 요청을 다시 실행 (retry 횟수 증가)
              request(method, endPoint, useToken, data, isMultipart, responseType, retry + 1)
                .then(resolve)
                .catch(reject);
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        const currentRefreshToken = getRefreshToken();
        if (!currentRefreshToken) {
          console.error('[Token Refresh] 에러 발생: 리프레시 토큰이 없습니다.');
          debugLog('TOKEN_REFRESH_MISSING_REFRESH_TOKEN', { endPoint });
          throw new Error('다시 로그인해주세요.');
        }

        console.log('[Token Refresh] 토큰 갱신 시도 중...');
        debugLog('TOKEN_REFRESH_REQUEST', { endPoint });
        // acepharm API 엔드포인트로 수정 필요
        const refreshResponse = await fetch(`${API_BASE_URL}/api/authentication/issue-token`, {
          method: 'POST',
          headers: {
            [API_HEADERS.CONTENT_TYPE]: API_CONTENT_TYPES.JSON,
          },
          body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        const refreshData = await refreshResponse.json();
        if (IS_DEV_ENV) {
          console.log('[Token Refresh] 상태:', {
            status: refreshResponse.status,
            ok: refreshResponse.ok,
            statusText: refreshResponse.statusText,
            url: refreshResponse.url,
          });
          console.log('[Token Refresh] 응답 데이터:', refreshData);
        }
        debugLog('TOKEN_REFRESH_RESPONSE', { endPoint, status: refreshResponse.status, body: refreshData });

        if (refreshData.isSuccess) {
          const newAccessToken = refreshData.result.accessToken;
          const newRefreshToken = refreshData.result.refreshToken;

          const newTokens = {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          };

          saveTokens(newTokens);
          console.log('[Token Refresh] 토큰 갱신 성공');
          debugLog('TOKEN_REFRESH_SUCCESS', {
            endPoint,
            accessTokenPreview: newAccessToken?.substring(0, 10),
            refreshTokenPreview: newRefreshToken?.substring(0, 10),
          });

          // Store 동기화 (콜백이 등록된 경우)
          if (onTokenRefreshCallback) {
            onTokenRefreshCallback(newTokens);
          }

          isRefreshing = false;
          processQueue();

          const retryHeaders = new Headers(headers);
          retryHeaders.set(API_HEADERS.AUTHORIZATION, `Bearer ${newAccessToken}`);

          console.log('[Token Refresh] 원래 요청 재시도 중...');
          debugLog('TOKEN_REFRESH_RETRY_ORIGINAL_REQUEST', { endPoint });
          return await request(method, endPoint, useToken, data, isMultipart, responseType, retry + 1);
        } else {
          console.error('[Token Refresh] 토큰 갱신 실패:', refreshData.message);
          debugLog('TOKEN_REFRESH_FAILED_RESPONSE', { endPoint, body: refreshData });
          throw new Error(refreshData.message);
        }
      } catch (refreshError: any) {
        console.error('Failed to refresh token:', refreshError);
        debugLog('TOKEN_REFRESH_EXCEPTION', { endPoint, error: refreshError });
        isRefreshing = false;
        processQueue(refreshError);

        // 토큰 삭제 및 상태 초기화
        clearTokens();

        redirectToLoginIfAllowed();

        return handleResponse({
          isSuccess: false,
          code: 401,
          message: '인증이 만료되었습니다. 다시 로그인해주세요.',
          result: null,
        });
      }
    }

    const finalResponse = handleResponse(responseData);

    // [전역 에러 핸들링] 삭제된 페이지(게시물) - QueryCache.onError 트리거를 위해 throw (GET 요청 시에만 수행)
    if (
      method === 'GET' &&
      !finalResponse.isSuccess &&
      (finalResponse.message === '값을 불러오는데 실패하였습니다.' ||
        finalResponse.message === '상품을 찾을 수 없습니다.')
    ) {
      throw new Error(finalResponse.message);
    }

    return finalResponse;
  } catch (err: unknown) {
    debugLog('REQUEST_EXCEPTION', { endPoint, error: err });
    if (err instanceof Error) {
      // [전역 에러 핸들링] 삭제된 페이지(게시물) 에러는 그대로 throw (GET 요청 시에만 수행)
      if (
        method === 'GET' &&
        (err.message === '값을 불러오는데 실패하였습니다.' || err.message === '상품을 찾을 수 없습니다.')
      ) {
        throw err;
      }

      // 404 에러인 경우 더 명확한 메시지
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        console.error(`[API Request] 404 에러 - 엔드포인트를 확인해주세요: ${endPoint}`);
        throw new Error(`요청한 엔드포인트를 찾을 수 없습니다: ${endPoint}`);
      }
      throw new Error(`API 요청 실패: ${err.message}`);
    }
    throw new Error('알 수 없는 에러 발생');
  }
};

// 이미지 요청 함수 (필요시)
const requestImage = async (method: 'GET', data?: any): Promise<Blob> => {
  let url = `${API_BASE_URL}/api/image`;
  const headers = new Headers({});

  if (method === 'GET' && data) {
    const queryStringData: Record<string, string> = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        queryStringData[key] = String(data[key]);
      }
    }
    const queryParams = new URLSearchParams(queryStringData).toString();
    url = `${url}?${queryParams}`;
  }

  const accessToken = getAuthToken();
  if (accessToken) {
    headers.append(API_HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  } catch (err: unknown) {
    if (err instanceof Error) {
      throw new Error(`이미지 API 요청 실패: ${err.message}`);
    }
    throw new Error('알 수 없는 에러 발생');
  }
};

// dasee 스타일의 requests 객체 - 완전히 동일하게
export const requests = {
  get: <T = any>(
    endPoint: string,
    useToken: boolean,
    query?: Record<string, string | number | boolean>,
    responseType?: 'json' | 'blob',
  ) => request<T>('GET', endPoint, useToken, query, undefined, responseType),
  post: <ResponseType = any, RequestType = any>(
    endPoint: string,
    useToken: boolean,
    body?: RequestType,
    isMultipart?: boolean,
  ) => request<ResponseType>('POST', endPoint, useToken, body, isMultipart),
  put: <T = any>(endPoint: string, useToken: boolean, body?: any, isMultipart?: boolean) =>
    request<T>('PUT', endPoint, useToken, body, isMultipart),
  patch: <T = any>(endPoint: string, useToken: boolean, body?: any, isMultipart?: boolean) =>
    request<T>('PATCH', endPoint, useToken, body, isMultipart),
  delete: <T = any>(endPoint: string, useToken: boolean, body?: any) => request<T>('DELETE', endPoint, useToken, body),
  getImage: (query?: Record<string, string | number | boolean>) => requestImage('GET', query),
};

export const api = {
  get: <T = any>(url: string, useToken: boolean = false, query?: Record<string, string | number | boolean>) =>
    requests.get<T>(url, useToken, query),
  post: <T = any>(url: string, data?: any, useToken: boolean = false, isMultipart?: boolean) =>
    requests.post<T>(url, useToken, data, isMultipart),
  put: (url: string, data?: any, useToken: boolean = false, isMultipart?: boolean) =>
    requests.put(url, useToken, data, isMultipart),
  patch: (url: string, data?: any, useToken: boolean = false, isMultipart?: boolean) =>
    requests.patch(url, useToken, data, isMultipart),
  delete: (url: string, useToken: boolean = false, body?: any) => requests.delete(url, useToken, body),
};

export { clearTokens, getAuthToken, loadTokens, saveTokens };
export type { ApiError, BaseResponse, ResponseType, TokenData };
export default requests;
